//* Vue
import type { SetupContext } from "vue";
//* 组件属性
import type { DiagramsEmits, DiagramsProps, DiagramsOption } from "../index";
import { contrast, get_key_option, get_sort_result, get_row_number, get_row_offset, get_row_options, get_row_header_options } from "./utils";
//* 样式
import css from "../index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组合函数
export const useComposable = (define: DiagramsProps, emits: SetupContext<typeof DiagramsEmits>["emit"]) => {
    //* 静态属性
    const vars: { observer?: ResizeObserver; timer?: NodeJS.Timeout } = {};

    //* 响应式属性
    const refs = {
        mask: ref<boolean>(false),
        sort: reactive({ key: "", value: "" }),
        search: reactive<{ [name: string]: string }>({}),
        checkbox: reactive<{ [name: string]: any[] }>({}),
        checkboxs: reactive<{ [name: string]: boolean }>({}),
    };

    //* 节点
    const nodes = {
        container: ref<HTMLDivElement>(),
    };

    //* 计算属性
    const computeds = {
        rows: computed(() => get_row_options(define.option) || []),
        colspans: computed(() => get_row_header_options(define.option, get_row_number(define.option), 0) || []),
    };

    //* 工具函数
    const utils = {
        //* 表格样式
        rows: (option: DiagramsOption): any => {
            if (!option.fixed)
                return { class: option.className, style: option.align == "left" ? { "text-align": "left" } : { "text-align": "center" } };
            else {
                //* 数据初始化
                const result = {
                    class: [css.fixed, option.className],
                    style: {
                        "left": get_row_offset(option.key, computeds.rows.value || []) + "px",
                        "text-align": option.align == "left" ? "left" : "center",
                    },
                };

                //* 检测是否最后一个，增加遮罩
                if (computeds.rows.value.filter((value) => value.fixed)?.slice(-1)[0]?.key == option.key) {
                    result.class.push(css.mask);
                }

                return result;
            }
        },

        //* 单元格尺寸
        size: (option: DiagramsOption) => {
            if (!option.width) return;
            else {
                return { width: `${option.width}px` };
            }
        },

        //* 单元格样式
        column: (data: any, index: number, option: DiagramsOption) => {
            //* 数据初始化
            const result = { rowspan: 1, colspan: 1 };
            const merges = option.merge?.(define.data, data, index) || {};
            if (Number(merges.rowspan) >= 0) result.rowspan = merges.rowspan || 0;
            if (Number(merges.colspan) >= 0) {
                result.colspan = merges.colspan || 0;
            }

            return Object.assign(result, utils.rows(option));
        },

        //* 页面滚动处理函数
        scroll: () => {
            if (!nodes.container.value) return;
            else {
                refs.mask.value = nodes.container.value.scrollLeft > 0;
            }
        },

        //* 尺寸变更处理函数
        resize: () => {
            //* 重置尺寸
            vars.timer && clearTimeout(vars.timer);
            vars.timer = setTimeout(() => {
                if (!nodes.container.value) return;
                else {
                    // nodes.container.value.style.whiteSpace = "nowrap";
                    nodes.container.value?.querySelectorAll(`.${css.column}`)?.forEach((node) => ((node as HTMLDivElement).style.width = "auto"));
                    requestAnimationFrame(() => {
                        computeds.rows.value?.forEach((row) => {
                            if (row.align != "singularity") return;
                            else {
                                const result = { size: 0 };
                                const columns = nodes.container.value?.querySelectorAll(`.${css.column}[data-key="${row.key}"]`);
                                columns?.forEach((column) => {
                                    result.size = Math.max(result.size, (column as HTMLDivElement).offsetWidth);
                                });

                                columns?.forEach((column) => {
                                    const col = column as HTMLDivElement;
                                    if (!col.getAttribute("data-thead")) col.style.width = result.size + 1 + "px";
                                });
                            }
                        });
                    });
                }
            }, 10);
        },
    };

    //* 函数列表
    const methods = {
        //* 初始化
        init: () => {
            //* singularity对齐函数
            if (vars.observer) utils.resize();
            //* 检测是否生成选择器数据
            computeds.colspans.value.forEach((colspan) => {
                colspan.forEach((row) => {
                    if (!refs.checkboxs[row.key]) {
                        refs.checkbox[row.key] = [];
                        refs.checkboxs[row.key] = false;
                    }
                });
            });
        },

        //* 排序
        sort: (key: string, reverse?: boolean, value?: "" | "asc" | "desc") => {
            switch (value) {
                case "":
                case "asc":
                case "desc": {
                    refs.sort.value = key;
                    refs.sort.value = value;
                    return;
                }

                default: {
                    //* 数据初始化
                    const direction = reverse ? -1 : 1;
                    const index = contrast.findIndex((value) => value == refs.sort.value);
                    //* 检测是否切换排序
                    if (refs.sort.key != key) {
                        refs.sort.key = key;
                        refs.sort.value = get_sort_result(direction);
                        break;
                    }

                    //* 检测排序类型
                    const result = get_sort_result(index + direction);
                    if (result) refs.sort.value = result;
                    else {
                        refs.sort.key = "";
                        refs.sort.value = "";
                    }

                    break;
                }
            }

            return emits("advanced", { sort: refs.sort, search: refs.search });
        },

        //* 检索
        search: () => {
            return emits("advanced", { sort: refs.sort, search: refs.search });
        },

        //* 单选
        checkbox: (key: string, value?: string) => {
            //* 数据增删
            if (!refs.checkbox[key]) return;
            else if (value) {
                if (refs.checkbox[key].includes(value)) refs.checkbox[key] = refs.checkbox[key].filter((v) => v != value);
                else {
                    refs.checkbox[key].push(value);
                }
            }

            //* 数据初始化
            const option = get_key_option(key, computeds.colspans.value)?.selectors || [];
            const result = { status: true, data: <{ key: string; value: any[] }[]>[] };
            for (let i = 0; i < option?.length; i++) {
                if (!refs.checkbox[key].includes(option[i].value)) {
                    result.status = false;
                    break;
                }
            }

            //* 数据更新
            refs.checkboxs[key] = result.status;
            //* 检索结果统计
            for (const i in refs.checkbox) {
                if (refs.checkbox[i].length) result.data.push({ key: i, value: refs.checkbox[i] });
            }

            return emits("advanced", { checkboxs: result.data });
        },

        //* 全选
        checkboxs: (key: string, value?: boolean) => {
            //* 数据更新
            if (typeof value == "boolean") refs.checkboxs[key] = value;
            if (refs.checkboxs[key]) {
                refs.checkbox[key] = get_key_option(key, computeds.colspans.value)?.selectors?.map((value) => value.value) || [];
            } else {
                refs.checkbox[key] = [];
            }

            //* 检索结果统计
            const result: { key: string; value: any[] }[] = [];
            for (const i in refs.checkbox) {
                if (refs.checkbox[i].length) result.push({ key: i, value: refs.checkbox[i] });
            }

            return emits("advanced", { checkboxs: result });
        },
    };

    //* 侦听器
    const watchs = {
        colspans: watch(computeds.colspans, methods.init, { immediate: true }),
    };

    //* 动态属性
    const binds = reactive({
        checkboxs: computed(() => {
            return { class: css_normalize.checkbox, value: "all" };
        }),
    });

    return { vars, refs, nodes, utils, binds, watchs, methods, computeds };
};
